/**
 * lib/assessmentConfig.ts
 * ------------------------------------------------------------------
 * Resolves the CA labels for a term from the assessmentConfig
 * collection, falling back to the hardcoded TERM_CA_MONTHS when no
 * config exists yet (so a fresh deployment isn't broken on day zero).
 *
 * Kept pure for testability: the caller passes the configs array
 * (from DataContext) and the term. No I/O, no React.
 *
 * IMPORTANT: deriveMark in grading.ts averages whatever values exist
 * under `ca` regardless of key. The labels here are purely a UI
 * concern + the key under which Mark Entry writes a value. Changing
 * labels does not change the math.
 * ------------------------------------------------------------------
 */
import { TERM_CA_MONTHS } from './grading';
import type {
  AssessmentConfigDocument,
  ExamTitleDocument,
  ReportNoticeDocument,
} from '@/types';

/**
 * Returns the ordered list of CA labels for the given term.
 *
 * Priority:
 *  1. A configured document with a non-empty caLabels list.
 *  2. The hardcoded TERM_CA_MONTHS default (e.g. January..April).
 *  3. Empty list (only if both above are absent — should be impossible
 *     for known terms but we never produce undefined behaviour).
 */
export function getCaLabels(
  term: string,
  configs: AssessmentConfigDocument[],
): string[] {
  const cfg = configs.find((c) => c.term === term);
  if (cfg && cfg.caLabels && cfg.caLabels.length > 0) {
    return cfg.caLabels;
  }
  const fallback = TERM_CA_MONTHS[term];
  return fallback ?? [];
}

/**
 * Validates a proposed CA labels list. Used by the admin panel before
 * saving — surfaces problems instead of writing garbage to Firestore.
 */
export interface ValidationResult {
  ok: boolean;
  errors: string[];
}
export function validateCaLabels(labels: string[]): ValidationResult {
  const errors: string[] = [];
  if (labels.length === 0) {
    errors.push('A term must have at least 1 CA.');
  }
  if (labels.length > 8) {
    errors.push('More than 8 CAs is almost certainly a mistake.');
  }
  // each label must be non-empty after trim
  for (let i = 0; i < labels.length; i++) {
    if (!labels[i] || labels[i].trim().length === 0) {
      errors.push(`CA #${i + 1} has no label.`);
    }
    if (labels[i] && labels[i].length > 40) {
      errors.push(`CA #${i + 1} label is too long (max 40 chars).`);
    }
  }
  // no duplicates (otherwise the `ca` map collapses)
  const seen = new Set<string>();
  for (const l of labels) {
    const key = (l ?? '').trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) {
      errors.push(`CA labels must be unique — "${l}" appears twice.`);
      break;
    }
    seen.add(key);
  }
  return { ok: errors.length === 0, errors };
}

/**
 * When admin wants to REDUCE the CA count or RENAME a label, we have
 * to detect whether existing marks data is in slots that would be
 * dropped. This pure function answers that question — given the old
 * labels (currently saved) and the new labels (being proposed) and a
 * set of "labels that have any student mark", return the list of
 * labels that would lose data.
 *
 * Renames are NOT data-losing on their own (we'd just be relabelling
 * the slot — but in this design the label IS the key, so a rename is
 * data-losing too unless we explicitly migrate). For step 1 the rule
 * is: if any label is removed from the list, AND that label has data,
 * confirm with the admin.
 */
export function findCaLabelsLosingData(
  oldLabels: string[],
  newLabels: string[],
  labelsWithData: Set<string>,
): string[] {
  const newSet = new Set(newLabels.map((l) => l.trim()));
  const dropped: string[] = [];
  for (const old of oldLabels) {
    if (!newSet.has(old.trim()) && labelsWithData.has(old)) {
      dropped.push(old);
    }
  }
  return dropped;
}

/**
 * STEP 2: Returns the exam-column title for a (form, term) pair.
 *
 * Looks up the configured ExamTitleDocument for the exact pair, and
 * falls back to the generic 'Exam' so the report card never renders
 * a blank header. Term 1 doesn't have a separate exam in LKC's
 * grading rule (caller checks `termUsesExam` first), but this
 * function still answers gracefully if asked.
 *
 * Form-level matching uses the SAME string the rest of the app uses
 * (getFormLevel returns '1' | '2' | ... | 'AS' | 'A2'), so an admin
 * configuring "5_Term2 -> Mock Exam" reaches Form 5 students
 * automatically — no class-by-class duplication.
 */
export function getExamTitle(
  formLevel: string | null,
  term: string,
  configs: ExamTitleDocument[],
): string {
  if (formLevel) {
    const hit = configs.find(
      (c) => c.formLevel === formLevel && c.term === term,
    );
    if (hit && hit.title && hit.title.trim().length > 0) {
      return hit.title.trim();
    }
  }
  return 'Exam';
}

/**
 * Validation for the exam-title admin form. Same shape as
 * validateCaLabels so the UI can render errors consistently.
 */
export function validateExamTitle(title: string): ValidationResult {
  const errors: string[] = [];
  const trimmed = (title ?? '').trim();
  if (trimmed.length === 0) {
    errors.push('Exam title cannot be empty.');
  }
  if (trimmed.length > 40) {
    errors.push('Exam title is too long (max 40 chars).');
  }
  return { ok: errors.length === 0, errors };
}

/**
 * STEP 3: Returns the (frontPage, backPage) notice for a (year, term).
 *
 * Pure helper. Returns empty strings when no document is configured,
 * so the renderer can decide whether to draw the block at all — the
 * agreed default for an unconfigured term is silent absence (no
 * placeholder text, no empty border).
 *
 * Whitespace is trimmed; truly-empty strings are returned for the
 * "no notice" case so consumers can do a simple `.length > 0` check.
 */
export function getReportNotice(
  year: string,
  term: string,
  configs: ReportNoticeDocument[],
): { frontPage: string; backPage: string } {
  const hit = configs.find(
    (c) => c.year === year && c.term === term,
  );
  return {
    frontPage: (hit?.frontPage ?? '').trim(),
    backPage: (hit?.backPage ?? '').trim(),
  };
}

/**
 * Validation for the notice admin form. Allows EMPTY (treated as
 * "no notice" — that's intentional, not a mistake). Cap at 500
 * chars per block, which is roughly what the PDF can render without
 * overflowing the available space.
 */
export function validateNotice(text: string): ValidationResult {
  const errors: string[] = [];
  if ((text ?? '').length > 500) {
    errors.push(
      'Notice is too long (max 500 characters). Trim it or split it across the front/back blocks.',
    );
  }
  return { ok: errors.length === 0, errors };
}

/** Max chars for a single notice block — enforced in admin UI. */
export const NOTICE_MAX_CHARS = 500;
